import {
    ClinicianRecommendationFragment,
    IncompatibleClinicianFragment,
} from '@/components/decisionSupport/queries/clinicianRecommendations.gql.types';
import { ConstraintCell } from '@/components/decisionSupport/tables/ConstraintCell';
import { NotAvailableCell } from '@/components/decisionSupport/tables/NotAvailableCell';
import { RecommendationCell } from '@/components/decisionSupport/tables/RecommendationCell';
import {
    DecisionSupportSelection,
    EMPTY_RESULT,
    NormalizedSlotResult,
    RecommendationRow,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { ColumnDef } from '@tanstack/react-table';
import { IncompatibilityType } from 'gql-types';

export const clinicianRecommendationToSelection = (rec: {
    clinician: { id: string; fullName: string };
}): DecisionSupportSelection => ({
    id: rec.clinician.id,
    label: rec.clinician.fullName,
});

export const findClinicianRecommendation = <T extends { clinician: { id: string } }>(
    recommendations: T[],
    selectedId: string | null | undefined,
): T | null => {
    if (!selectedId) {
        return null;
    }
    return recommendations.find((rec) => rec.clinician.id === selectedId) ?? null;
};

// Recommended clinician column ids.
// Tried to maintain consistency with ScoringFactorDefinition's used in careTeamRecommendations.utils.tsx.
// Eventually this will come from the BE and we can remove this object.
export const FACTOR_CODES = {
    careTeam: 'careTeamScore',
    notFull: 'wouldClinicianBeOverfullByAmortizedVisitDuration',
    shiftTerritoryCoverage: 'hasShiftWithTerritoryCoverage',
    distance: 'distanceFromFutureVisitsCentroidMi',
    caseload: 'spareShiftHoursNormalizedScore',
    compensationType: 'compensationTypeScore',
    language: 'satisfiesLanguagePreference',
    gender: 'satisfiesGenderPreference',
    continuity: 'continuityOfCareScore',
} as const;

// Recommended clinician column labels.
export const FACTOR_LABELS: Record<(typeof FACTOR_CODES)[keyof typeof FACTOR_CODES], string> = {
    [FACTOR_CODES.careTeam]: 'Care Team',
    [FACTOR_CODES.notFull]: 'Not Full',
    [FACTOR_CODES.shiftTerritoryCoverage]: 'Shift In Territory',
    [FACTOR_CODES.distance]: 'Distance',
    [FACTOR_CODES.caseload]: 'Caseload',
    [FACTOR_CODES.compensationType]: 'Comp Structure',
    [FACTOR_CODES.language]: 'Language Preference',
    [FACTOR_CODES.gender]: 'Gender Preference',
    [FACTOR_CODES.continuity]: 'Continuity of Care',
};

const INCOMPATIBILITY_TYPE_LABELS: Record<IncompatibilityType, string> = {
    [IncompatibilityType.CLINICIAN_ACTIVE]: 'Active',
    [IncompatibilityType.CLINICIAN_ADDRESS_SERVICESET_GEOGRAPHY]: 'Service Geography',
    [IncompatibilityType.CLINICIAN_DISCIPLINE]: 'Discipline',
    [IncompatibilityType.CLINICIAN_GEOGRAPHIC_INCOMPATIBLE]: 'Geography',
    [IncompatibilityType.CLINICIAN_HAS_SHIFT]: 'Has Shift',
    [IncompatibilityType.CLINICIAN_LICENSE]: 'License',
    [IncompatibilityType.CLINICIAN_PATIENT_PAYER]: 'Patient Payer',
    [IncompatibilityType.CLINICIAN_PATIENT_SERVICESET_SEX]: 'Sex',
    [IncompatibilityType.CLINICIAN_SERVICESET_LICENSE]: 'Service License',
    [IncompatibilityType.CLINICIAN_SERVICESET_PAYER]: 'Service Payer',
    [IncompatibilityType.CLINICIAN_SERVICESET_QUALIFICATION]: 'Qualification',
    [IncompatibilityType.CLINICIAN_SHIFT_QUALIFICATION]: 'Shift Qualification',
    [IncompatibilityType.CLINICIAN_SHIFT_TERRITORY]: 'Territory',
    [IncompatibilityType.SERVICESET_ADDRESS_GEOGRAPHY]: 'Address Geography',
    [IncompatibilityType.SERVICESET_PATIENT_PAYER]: 'Patient Payer (Service)',
};

const numberFromBoolean = (value: boolean): number => (value ? 100 : 0);

// Normalize a clinician recommendation to a RecommendationRow.
// The per-factor logic is what blocks a clean loop.
// Each factor pulls from a different field with different transforms.
const normalizeRecommendation = (rec: ClinicianRecommendationFragment): RecommendationRow => ({
    id: rec.clinician.id,
    label: rec.clinician.fullName,
    recommendationRank: rec.recommendationRank,
    factors: {
        [FACTOR_CODES.careTeam]: {
            sortValue: rec.careTeamScore,
            displayValue: '',
            isSatisfied: null,
        },
        [FACTOR_CODES.notFull]: {
            sortValue: numberFromBoolean(!rec.wouldClinicianBeOverfullByAmortizedVisitDuration),
            displayValue: '',
            isSatisfied: null,
        },
        [FACTOR_CODES.shiftTerritoryCoverage]: {
            sortValue: numberFromBoolean(rec.hasShiftWithTerritoryCoverage),
            displayValue: '',
            isSatisfied: null,
        },
        [FACTOR_CODES.distance]: {
            sortValue: rec.distanceFromFutureVisitsCentroidMi,
            displayValue: '',
            isSatisfied: null,
        },
        [FACTOR_CODES.caseload]: {
            sortValue: rec.spareShiftHoursNormalizedScore,
            displayValue: '',
            isSatisfied: null,
        },
        [FACTOR_CODES.compensationType]: {
            sortValue: rec.compensationTypeScore,
            displayValue: '',
            isSatisfied: null,
        },
        [FACTOR_CODES.language]: {
            sortValue: numberFromBoolean(rec.satisfiesLanguagePreference),
            displayValue: '',
            isSatisfied: null,
        },
        [FACTOR_CODES.gender]: {
            sortValue: numberFromBoolean(rec.satisfiesGenderPreference),
            displayValue: '',
            isSatisfied: null,
        },
        [FACTOR_CODES.continuity]: {
            sortValue: rec.continuityOfCareScore === 1 ? 100 : 0,
            displayValue: '',
            isSatisfied: null,
        },
    },
});

const normalizeIncompatible = (ic: IncompatibleClinicianFragment): RecommendationRow => {
    const failuresByType = new Map(
        ic.compatibilityResult.incompatibilities.map((i) => [i.type, i.description]),
    );
    const factors: RecommendationRow['factors'] = {};
    Object.values(IncompatibilityType).forEach((type) => {
        const failureDescription = failuresByType.get(type);
        factors[type] = {
            sortValue: null,
            displayValue: failureDescription ?? '',
            isSatisfied: failureDescription === undefined,
        };
    });
    return {
        id: ic.clinician.id,
        label: ic.clinician.fullName,
        recommendationRank: Infinity,
        factors,
    };
};

const factorValue = (row: RecommendationRow, code: string): number | null => {
    return row.factors[code]?.sortValue ?? null;
};

const buildRecommendedColumns = (rows: RecommendationRow[]): ColumnDef<RecommendationRow>[] => {
    const distances = rows
        .map((r) => factorValue(r, FACTOR_CODES.distance))
        .filter((v): v is number => v !== null);
    const distanceRange: [number, number] =
        distances.length > 0 ? [Math.min(...distances), Math.max(...distances)] : [0, 0];

    const hasAnyCompensationType = rows.some(
        (r) => factorValue(r, FACTOR_CODES.compensationType) !== null,
    );

    const baseColumns: ColumnDef<RecommendationRow>[] = [
        {
            id: FACTOR_CODES.careTeam,
            header: FACTOR_LABELS[FACTOR_CODES.careTeam],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.careTeam);
                return v === null ? null : <RecommendationCell value={v} />;
            },
        },
        {
            id: FACTOR_CODES.notFull,
            header: FACTOR_LABELS[FACTOR_CODES.notFull],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.notFull);
                return v === null ? null : <RecommendationCell value={v} />;
            },
        },
        {
            id: FACTOR_CODES.shiftTerritoryCoverage,
            header: FACTOR_LABELS[FACTOR_CODES.shiftTerritoryCoverage],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.shiftTerritoryCoverage);
                return v === null ? null : <RecommendationCell value={v} />;
            },
        },
        {
            id: FACTOR_CODES.distance,
            header: FACTOR_LABELS[FACTOR_CODES.distance],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.distance);
                return v === null ? null : (
                    <RecommendationCell isInverse value={v} range={distanceRange} roundedTo={25} />
                );
            },
        },
        {
            id: FACTOR_CODES.caseload,
            header: FACTOR_LABELS[FACTOR_CODES.caseload],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.caseload);
                return v === null ? null : (
                    <RecommendationCell value={v} range={[0, 100]} roundedTo={25} />
                );
            },
        },
    ];

    if (hasAnyCompensationType) {
        baseColumns.push({
            id: FACTOR_CODES.compensationType,
            header: FACTOR_LABELS[FACTOR_CODES.compensationType],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.compensationType);
                return v === null ? <NotAvailableCell /> : <RecommendationCell value={v} />;
            },
        });
    }

    baseColumns.push(
        {
            id: FACTOR_CODES.language,
            header: FACTOR_LABELS[FACTOR_CODES.language],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.language);
                return v === null ? null : <RecommendationCell value={v} />;
            },
        },
        {
            id: FACTOR_CODES.gender,
            header: FACTOR_LABELS[FACTOR_CODES.gender],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.gender);
                return v === null ? null : <RecommendationCell value={v} />;
            },
        },
        {
            id: FACTOR_CODES.continuity,
            header: FACTOR_LABELS[FACTOR_CODES.continuity],
            cell: ({ row }) => {
                const v = factorValue(row.original, FACTOR_CODES.continuity);
                return v === null ? null : <RecommendationCell value={v} />;
            },
        },
    );

    return baseColumns;
};

const ALWAYS_VISIBLE_INCOMPATIBILITY_TYPES: IncompatibilityType[] = [
    IncompatibilityType.CLINICIAN_GEOGRAPHIC_INCOMPATIBLE,
    IncompatibilityType.CLINICIAN_SERVICESET_LICENSE,
];

const buildNotRecommendedColumns = (rows: RecommendationRow[]): ColumnDef<RecommendationRow>[] => {
    const triggered = new Set<IncompatibilityType>();
    rows.forEach((row) => {
        Object.values(IncompatibilityType).forEach((type) => {
            if (row.factors[type]?.isSatisfied === false) {
                triggered.add(type);
            }
        });
    });
    const orderedTypes: IncompatibilityType[] = [
        ...ALWAYS_VISIBLE_INCOMPATIBILITY_TYPES,
        ...Object.values(IncompatibilityType).filter(
            (type) => !ALWAYS_VISIBLE_INCOMPATIBILITY_TYPES.includes(type) && triggered.has(type),
        ),
    ];
    return orderedTypes.map(
        (type): ColumnDef<RecommendationRow> => ({
            id: type,
            header: INCOMPATIBILITY_TYPE_LABELS[type],
            cell: ({ row }) => {
                const factor = row.original.factors[type];
                if (!factor || factor.isSatisfied === null) {
                    return null;
                }
                return <ConstraintCell isSatisfied={factor.isSatisfied} />;
            },
        }),
    );
};

export type ClinicianRecommendationsInput = {
    recommendations: ClinicianRecommendationFragment[];
    incompatibleClinicians: IncompatibleClinicianFragment[];
};

export const normalizeClinicianRecommendations = (
    input: ClinicianRecommendationsInput | null,
): NormalizedSlotResult => {
    if (!input) {
        return EMPTY_RESULT;
    }
    const recommended = input.recommendations
        .slice()
        .sort((a, b) => a.recommendationRank - b.recommendationRank)
        .map(normalizeRecommendation);
    const notRecommended = input.incompatibleClinicians.map(normalizeIncompatible);
    return {
        recommended,
        notRecommended,
        recommendedColumns: buildRecommendedColumns(recommended),
        notRecommendedColumns: buildNotRecommendedColumns(notRecommended),
    };
};
